/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PaipReasonProps {
  id: string;
  name: string;
  outOfServiceReasonId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PaipReasonRecord extends Record<PaipReasonProps>({
  id: null as any,
  name: null as any,
  outOfServiceReasonId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    outOfServiceReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PaipReasonRecord {
    if (json == null) {
      throw new Error('PaipReasonRecord.fromJS: parameter cannot be null');
    }
    return new PaipReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PaipReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'PaipReasonJson.name'),
      outOfServiceReasonId: json.outOfServiceReasonId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    outOfServiceReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PaipReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('PaipReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PaipReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PaipReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'PaipReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceReasonId: tryOrDefault(
        () => json.outOfServiceReasonId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PaipReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('PaipReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PaipReasonViewModelBase implements PaipReasonProps {
  protected readonly _props: PaipReasonProps;
  private _json?: PaipReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get outOfServiceReasonId(): string | null {
    return this._props.outOfServiceReasonId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PaipReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        outOfServiceReasonId: this._props.outOfServiceReasonId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPaipReasonEntities = (state: AppState) =>
  state.entityStores.paipReason.entities;

export const selectPaipReasonById = (id: string) => (state: AppState) =>
  state.entityStores.paipReason.entities.get(id);

export const selectPaipReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.paipReason.meta.get(id);

export const selectPaipReasonAndMetadataById = (id: string) =>
  createSelector(
    selectPaipReasonById(id),
    selectPaipReasonMetadataById(id),
    (paipReason, meta) => ({ paipReason, meta }),
  );

export function selectPaipReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPaipReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PaipReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPaipReasonEntitiesByName(name: string) {
  return createSelector(selectAllPaipReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectPaipReasonEntitiesByOutOfServiceReasonId(outOfServiceReasonId: string) {
  return createSelector(selectAllPaipReasonEntities, (entities) =>
    entities.filter((e) => e.outOfServiceReasonId === outOfServiceReasonId).valueSeq(),
  );
}
//#endregion Selectors
